import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { APARTMENTS } from "@/data/apartments";
import { useT } from "@/i18n/LanguageProvider";

const DOT = {
    verfuegbar: "bg-mint-300",
    reserviert: "bg-terracotta-300",
    verkauft: "bg-white/40",
};

/**
 * ApartmentStickyBar – slim quick-pick bar pinned to the bottom of the
 * apartments page. One chip per unit; clicking opens the detail dialog
 * (Apartments.jsx listens for the `g21:open-apartment` event).
 */
export const ApartmentStickyBar = () => {
    const t = useT();
    const [statusMap, setStatusMap] = useState({});
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        api.get("/apartments/availability")
            .then((r) => {
                const map = {};
                (r.data || []).forEach((a) => { map[a.id] = a.status; });
                setStatusMap(map);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        let raf = 0;
        const onScroll = () => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
                raf = 0;
                const sec = document.getElementById("wohnungen");
                const footer = document.querySelector("footer");
                if (!sec) return;
                const past = sec.getBoundingClientRect().top < 120;
                const footerNear = footer ? footer.getBoundingClientRect().top < window.innerHeight - 60 : false;
                setVisible(past && !footerNear);
            });
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
    }, []);

    const openApartment = (apt) => {
        window.dispatchEvent(new CustomEvent("g21:open-apartment", { detail: { apartmentId: apt.id } }));
    };

    return (
        <div
            className={`fixed bottom-4 inset-x-0 z-40 flex justify-center px-4 pointer-events-none transition-all duration-500 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
            aria-hidden={!visible}
            data-testid="apartment-sticky-bar"
        >
            <div
                className={`${visible ? "pointer-events-auto" : "pointer-events-none"} flex items-center gap-0.5 rounded-full bg-sage-600/95 backdrop-blur-md text-white shadow-[0_12px_40px_rgba(40,60,50,0.35)] px-2 py-1.5 max-w-full overflow-x-auto no-scrollbar`}
            >
                <span className="hidden lg:inline px-3 font-body text-[10px] uppercase tracking-[0.2em] text-white/55 whitespace-nowrap">
                    {t({ de: "Schnellwahl", en: "Quick pick" })}
                </span>
                {APARTMENTS.map((apt) => {
                    const status = statusMap[apt.id] || "verfuegbar";
                    const sold = status === "verkauft";
                    return (
                        <button
                            key={apt.id}
                            type="button"
                            onClick={() => openApartment(apt)}
                            className={`group flex items-center gap-2 rounded-full px-3.5 sm:px-4 py-2 whitespace-nowrap font-body text-sm transition-colors ${
                                sold ? "text-white/45 hover:bg-white/5" : "text-white hover:bg-white/10"
                            }`}
                            title={`${apt.name} · ${status === "verfuegbar" ? t({ de: "verfügbar", en: "available" }) : status === "reserviert" ? t({ de: "reserviert", en: "reserved" }) : t({ de: "verkauft", en: "sold" })}`}
                            data-testid={`sticky-apt-${apt.id}`}
                        >
                            <span className={`w-2 h-2 rounded-full shrink-0 ${DOT[status] || DOT.verfuegbar} ${status === "verfuegbar" ? "animate-pulse" : ""}`} />
                            <span className="font-semibold">{apt.shortName}</span>
                            <span className="hidden md:inline text-white/65 text-xs">
                                {apt.size} m² · {apt.rooms} {t({ de: "Zi.", en: "rm" })}
                            </span>
                            <ArrowUpRight size={13} className="hidden sm:block opacity-0 group-hover:opacity-100 -ml-0.5 transition-opacity" />
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
